import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { PatientResponse, BatchConfig, StrategyWithVersions } from "@/lib/types";
import { useOrganizationsStore } from "@/stores/organizationsStore";

interface ScheduleBatchCallsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patients: PatientResponse[];
  verifiedPhoneNumberId: string;
  onSuccess?: () => void;
}

// US states for holiday configuration
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// Days of week for batch scheduling
const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const ScheduleBatchCallsDialog: React.FC<ScheduleBatchCallsDialogProps> = ({
  open,
  onOpenChange,
  patients,
  verifiedPhoneNumberId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isScheduling, setIsScheduling] = useState(false);
  const [strategies, setStrategies] = useState<StrategyWithVersions[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>("");
  const [selectedStrategyVersionId, setSelectedStrategyVersionId] = useState<string>("");
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const { selectedOrganizationId } = useOrganizationsStore();

  // Start datetime for first batch
  const [startDateTime, setStartDateTime] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });

  // Batch configuration
  const [batchConfig, setBatchConfig] = useState<BatchConfig>({
    batch_size: 20,
    interval_hours: 1.5,
    allowed_days: [1, 2, 3, 4, 5], // Monday to Friday
    business_hours_start: 8,
    business_hours_end: 17,
    business_hours_timezone: "US/Central",
    flexible_time_window: true,
    timezone_name: "America/Chicago",
    skip_holidays: true,
    holiday_state: "TX",
    fill_batches_with_valid_patients: true,
  });

  // Fetch strategies when dialog opens
  useEffect(() => {
    if (open && selectedOrganizationId) {
      fetchStrategies();
    }
  }, [open, selectedOrganizationId]);

  const fetchStrategies = async () => {
    if (!selectedOrganizationId) return;

    try {
      setIsLoadingStrategies(true);
      const strategiesData = await api.getOrganizationStrategies(selectedOrganizationId);
      setStrategies(strategiesData);
    } catch (error) {
      console.error("Error fetching strategies:", error);
      toast({
        title: "Error",
        description: "Failed to load strategies. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingStrategies(false);
    }
  };

  const handleScheduleCalls = async () => {
    if (!selectedOrganizationId || !verifiedPhoneNumberId || patients.length === 0 || !selectedStrategyVersionId) {
      toast({
        title: "Error",
        description: "Missing required information for scheduling calls. Please select a strategy and version.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsScheduling(true);

      const patientIds = patients.map((patient) => patient.id);

      await api.scheduleCallsInBatches(
        patientIds,
        selectedOrganizationId,
        selectedStrategyVersionId,
        verifiedPhoneNumberId,
        startDateTime,
        batchConfig
      );

      toast({
        title: "Success",
        description: `Batch scheduling initiated for ${patientIds.length} patients`,
        variant: "default",
      });

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Error scheduling batch calls:", error);
      toast({
        title: "Error",
        description: "Failed to schedule batch calls. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  // Get a description of the patients being scheduled
  const getPatientsDescription = () => {
    if (patients.length > 3) {
      return `${patients.length} selected patients`;
    } else {
      return patients.map((p) => `${p.first_name} ${p.last_name}`).join(", ");
    }
  };

  // Handle allowed days checkbox changes
  const handleAllowedDaysChange = (day: number, checked: boolean) => {
    setBatchConfig((prev) => ({
      ...prev,
      allowed_days: checked
        ? [...prev.allowed_days, day].sort()
        : prev.allowed_days.filter((d) => d !== day),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Calls in Batches</DialogTitle>
          <DialogDescription>
            Schedule calls for {getPatientsDescription()} with configurable batch timing, business hours, and holiday restrictions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Strategy Selection */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="strategy" className="sm:text-right">
              Strategy
            </Label>
            <Select
              value={selectedStrategyId}
              onValueChange={(value) => {
                setSelectedStrategyId(value);
                setSelectedStrategyVersionId(""); // Reset version when strategy changes
              }}
              disabled={isLoadingStrategies}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder={isLoadingStrategies ? "Loading strategies..." : "Select strategy"} />
              </SelectTrigger>
              <SelectContent>
                {strategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="strategyVersion" className="sm:text-right">
              Version
            </Label>
            <Select
              value={selectedStrategyVersionId}
              onValueChange={setSelectedStrategyVersionId}
              disabled={!selectedStrategyId}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder={!selectedStrategyId ? "Select strategy first" : "Select version"} />
              </SelectTrigger>
              <SelectContent>
                {selectedStrategyId &&
                  strategies
                    .find((s) => s.id === selectedStrategyId)
                    ?.versions.map((version) => (
                      <SelectItem key={version.id} value={version.id}>
                        {version.version_description || `Version ${version.id.slice(-8)}`}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date & Time */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="startDateTime" className="sm:text-right">
              Start Date & Time
            </Label>
            <Input
              id="startDateTime"
              type="datetime-local"
              className="sm:col-span-3"
              value={startDateTime}
              onChange={(e) => setStartDateTime(e.target.value)}
              required
            />
          </div>

          {/* Batch Configuration */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="batchSize" className="sm:text-right">
              Batch Size
            </Label>
            <Input
              id="batchSize"
              type="number"
              className="sm:col-span-3"
              value={batchConfig.batch_size}
              onChange={(e) =>
                setBatchConfig((prev) => ({ ...prev, batch_size: parseInt(e.target.value) || 20 }))
              }
              min="1"
              max="100"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="intervalHours" className="sm:text-right">
              Interval (Hours)
            </Label>
            <Input
              id="intervalHours"
              type="number"
              step="0.5"
              className="sm:col-span-3"
              value={batchConfig.interval_hours}
              onChange={(e) =>
                setBatchConfig((prev) => ({ ...prev, interval_hours: parseFloat(e.target.value) || 1.5 }))
              }
              min="0.5"
              max="24"
              required
            />
          </div>

          {/* Allowed Days */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-start sm:gap-4">
            <Label className="sm:text-right sm:pt-2">Allowed Days</Label>
            <div className="sm:col-span-3 grid grid-cols-2 gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <div key={day.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day.value}`}
                    checked={batchConfig.allowed_days.includes(day.value)}
                    onCheckedChange={(checked) => handleAllowedDaysChange(day.value, checked as boolean)}
                  />
                  <Label htmlFor={`day-${day.value}`} className="text-sm">
                    {day.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Business Hours */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="businessHoursStart" className="sm:text-right">
              Business Hours Start
            </Label>
            <Input
              id="businessHoursStart"
              type="number"
              className="sm:col-span-3"
              value={batchConfig.business_hours_start}
              onChange={(e) =>
                setBatchConfig((prev) => ({ ...prev, business_hours_start: parseInt(e.target.value) || 8 }))
              }
              min="0"
              max="23"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="businessHoursEnd" className="sm:text-right">
              Business Hours End
            </Label>
            <Input
              id="businessHoursEnd"
              type="number"
              className="sm:col-span-3"
              value={batchConfig.business_hours_end}
              onChange={(e) =>
                setBatchConfig((prev) => ({ ...prev, business_hours_end: parseInt(e.target.value) || 17 }))
              }
              min="0"
              max="23"
              required
            />
          </div>

          {/* Timezones */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="businessHoursTimezone" className="sm:text-right">
              Business Hours Timezone
            </Label>
            <Select
              value={batchConfig.business_hours_timezone}
              onValueChange={(value) => setBatchConfig((prev) => ({ ...prev, business_hours_timezone: value }))}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US/Eastern">Eastern Time</SelectItem>
                <SelectItem value="US/Central">Central Time</SelectItem>
                <SelectItem value="US/Mountain">Mountain Time</SelectItem>
                <SelectItem value="US/Pacific">Pacific Time</SelectItem>
                <SelectItem value="US/Alaska">Alaska Time</SelectItem>
                <SelectItem value="US/Hawaii">Hawaii Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="timezone" className="sm:text-right">
              Timezone
            </Label>
            <Select
              value={batchConfig.timezone_name}
              onValueChange={(value) => setBatchConfig((prev) => ({ ...prev, timezone_name: value }))}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">Eastern Time</SelectItem>
                <SelectItem value="America/Chicago">Central Time</SelectItem>
                <SelectItem value="America/Denver">Mountain Time</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                <SelectItem value="America/Anchorage">Alaska Time</SelectItem>
                <SelectItem value="Pacific/Honolulu">Hawaii Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Holiday Configuration */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="holidayState" className="sm:text-right">
              Holiday State
            </Label>
            <Select
              value={batchConfig.holiday_state}
              onValueChange={(value) => setBatchConfig((prev) => ({ ...prev, holiday_state: value }))}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="flexible" className="sm:text-right">
              Flexible Time
            </Label>
            <div className="flex items-center space-x-2 sm:col-span-3">
              <Checkbox
                id="flexible"
                checked={batchConfig.flexible_time_window}
                onCheckedChange={(checked) =>
                  setBatchConfig((prev) => ({ ...prev, flexible_time_window: checked as boolean }))
                }
              />
              <Label htmlFor="flexible" className="text-sm font-medium leading-none">
                Allow flexible timing around scheduled times
              </Label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="skipHolidays" className="sm:text-right">
              Skip Holidays
            </Label>
            <div className="flex items-center space-x-2 sm:col-span-3">
              <Checkbox
                id="skipHolidays"
                checked={batchConfig.skip_holidays}
                onCheckedChange={(checked) =>
                  setBatchConfig((prev) => ({ ...prev, skip_holidays: checked as boolean }))
                }
              />
              <Label htmlFor="skipHolidays" className="text-sm font-medium leading-none">
                Skip holidays and move to next business day
              </Label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="fillBatches" className="sm:text-right">
              Fill Batches
            </Label>
            <div className="flex items-center space-x-2 sm:col-span-3">
              <Checkbox
                id="fillBatches"
                checked={batchConfig.fill_batches_with_valid_patients}
                onCheckedChange={(checked) =>
                  setBatchConfig((prev) => ({ ...prev, fill_batches_with_valid_patients: checked as boolean }))
                }
              />
              <Label htmlFor="fillBatches" className="text-sm font-medium leading-none">
                Fill batches with valid patients only
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isScheduling}>
            Cancel
          </Button>
          <Button onClick={handleScheduleCalls} disabled={isScheduling}>
            {isScheduling ? "Scheduling..." : "Schedule Batch Calls"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleBatchCallsDialog;